// Controls.jsx
import React, { useEffect, useState } from 'react';
import { useRoomContext } from '@livekit/components-react';
import { Track, DataPacket_Kind } from 'livekit-client';
import { ENDPOINTS } from '../endpoints';
import { useLiveKitSession } from '../LiveKitSessionContext';
import { authJsonHeaders } from '../telemetryApi';
import { shouldOfferAudioOutputPicker } from '../audioOutputSupport';

function isLikelyIOS() {
    if (typeof navigator === 'undefined') return false;
    if (/iPad|iPhone|iPod/i.test(navigator.userAgent || '')) return true;
    return navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
}

export default function Controls({
    screenTrack,
    isRecordingActive,
    recordingStarterIdentity,
    audioSinkId,
    onAudioSinkIdChange,
    /** From Room: enumerated audiooutput devices (refreshed after join). */
    audioOutputDevices = [],
    onToggleChat,
    setChatOpen,
    unreadCount,
    setUnreadCount,
    chatOpen,
    onStartRecording,
    onStopRecording,
    onRecordingStoppedMessage,
}) {
    const room = useRoomContext();
    const { telemetryJwt } = useLiveKitSession();
    const local = room?.localParticipant;
    const showAudioOutputPicker = shouldOfferAudioOutputPicker();

    // Sync with local participant's actual mic/cam state
    const [micOn, setMicOn] = useState(() => local?.isMicrophoneEnabled ?? true);
    const [camOn, setCamOn] = useState(() => local?.isCameraEnabled ?? true);
    const [recording, setRecording] = useState(false);
    const [egressId, setEgressId] = useState(null);
    const [wakeLock, setWakeLock] = useState(null);
    const [wakeLockNotice, setWakeLockNotice] = useState(null);

    useEffect(() => {
        return () => {
            wakeLock?.release?.();
        };
    }, [wakeLock]);

    useEffect(() => {
        if (!wakeLockNotice) return;
        const t = window.setTimeout(() => setWakeLockNotice(null), 8000);
        return () => window.clearTimeout(t);
    }, [wakeLockNotice]);

    useEffect(() => {
        if (!local) return;

        // Set initial state from current tracks
        const syncFromTracks = () => {
            const micPub = local.getTrack(Track.Source.Microphone);
            const camPub = local.getTrack(Track.Source.Camera);
            if (micPub) setMicOn(!micPub.isMuted);
            // Camera: we unpublish when "off" (new track_sid each time you turn on) — "on" iff published
            setCamOn(Boolean(camPub?.track));
        };
        syncFromTracks();

        const update = (pub) => {
            if (pub.source === Track.Source.Microphone) {
                setMicOn(!pub.isMuted);
            }
            if (pub.source === Track.Source.Camera) {
                setCamOn(Boolean(local.getTrack(Track.Source.Camera)?.track));
            }
        };

        local.on('localTrackPublished', update);
        local.on('trackMuted', update);
        local.on('trackUnmuted', update);
        local.on('localTrackUnpublished', update);

        return () => {
            local.off('localTrackPublished', update);
            local.off('trackMuted', update);
            local.off('trackUnmuted', update);
            local.off('localTrackUnpublished', update);
        };
    }, [local]);

    const toggleMic = async () => {
        try {
            await local.setMicrophoneEnabled(!micOn);
        } catch (err) {
            console.error('toggleMic failed', err);
            alert(
                'Microphone could not be enabled. Check browser permissions. On iPhone/iPad use Safari, allow mic access, and ensure the site is served over HTTPS.'
            );
        }
    };

    /**
     * Camera off → unpublish (ends egress / new file per LiveKit track_sid when you turn on again).
     * Camera on → publish a new camera track. Mic still uses mute/unmute only.
     */
    const toggleCam = async () => {
        try {
            if (camOn) {
                const camPub = local.getTrack(Track.Source.Camera);
                if (camPub?.track) {
                    await local.unpublishTrack(camPub.track);
                }
            } else {
                await local.setCameraEnabled(true);
            }
        } catch (err) {
            console.error('toggleCam failed', err);
            alert(
                'Camera could not be enabled. Check browser permissions. On iPhone/iPad use Safari, allow camera access, and ensure the site is served over HTTPS.'
            );
        }
    };

    const isLocalScreenSharer =
        screenTrack?.participant?.isLocal ?? false;

    const toggleScreen = async () => {
        try {
            await local.setScreenShareEnabled(!isLocalScreenSharer);
        } catch (err) {
            console.error('toggleScreen failed', err);
            alert(
                'Screen sharing could not start. On iPhone/iPad, use Safari and try again from a direct tap on Share. Some mobile browsers do not support screen capture.'
            );
        }
    };

    const startRecording = async () => {
        if (!room?.localParticipant) return;
        if (!telemetryJwt) {
            alert('Connecting to session services… try again in a moment.');
            return;
        }
        if (room.participants.size === 0) {
            alert('Wait for participants before recording');
            return;
        }

        const hasTracks = Array.from(room.localParticipant.tracks.values()).some((p) => p.track);
        if (!hasTracks) {
            alert('Camera or mic must be ON before recording');
            return;
        }

        try {
            const res = await fetch(ENDPOINTS.RECORD_START, {
                method: 'POST',
                headers: authJsonHeaders(telemetryJwt),
                body: JSON.stringify({ roomName: room.name, requestedBy: room.localParticipant.identity }),
            });
            const text = await res.text();
            if (!res.ok) {
                let message = 'Recording could not be started.';
                try {
                    const body = JSON.parse(text);
                    const errText = body.error || body.message || body.detail;
                    if (typeof errText === 'string' && errText.trim()) {
                        if (/already active|already running/i.test(errText)) {
                            message = 'A recording is already in progress. Only one recording can run at a time.';
                        } else {
                            message = errText;
                        }
                    }
                } catch (_) {
                    if (text && text.length < 200 && !text.startsWith('{')) message = text;
                }
                throw new Error(message);
            }
            const data = JSON.parse(text);
            // Server may return egressIds (array) or egressId (string)
            const id = data.egressIds?.[0] ?? data.egressIds ?? data.egressId ?? null;
            setEgressId(id);
            setRecording(true);
            onStartRecording?.();
            // Broadcast recording status so all participants see the indicator
            try {
                room.localParticipant.publishData(
                    new TextEncoder().encode(
                        JSON.stringify({
                            type: 'recordingStatus',
                            recording: true,
                            starterIdentity: room.localParticipant.identity,
                        })
                    ),
                    DataPacket_Kind.RELIABLE
                );
            } catch (e) {
                console.warn('Failed to broadcast recording started', e);
            }
        } catch (err) {
            console.error('Start recording failed', err);
            alert(err?.message || 'Recording could not be started. Please try again.');
        }
    };

    const stopRecording = async () => {
        if (!room) return;
        if (!telemetryJwt) {
            alert('Connecting to session services… try again in a moment.');
            return;
        }
        try {
            const res = await fetch(ENDPOINTS.RECORD_STOP, {
                method: 'POST',
                headers: authJsonHeaders(telemetryJwt),
                body: JSON.stringify({
                    egressIds: egressId != null ? (Array.isArray(egressId) ? egressId : [egressId]) : [],
                    roomName: room.name,
                    requestedBy: room.localParticipant.identity,
                }),
            });
            const text = await res.text();
            let body;
            try { body = text ? JSON.parse(text) : {}; } catch (_) { body = {}; }
            if (res.status === 202 && body?.requiresApproval) {
                try {
                    await room.localParticipant.publishData(
                        new TextEncoder().encode(JSON.stringify({
                            type: 'recordingStopApprovalRequest',
                            roomName: room.name,
                            requestId: body.requestId,
                            starterIdentity: body.starterIdentity,
                            requestedBy: room.localParticipant.identity,
                        })),
                        DataPacket_Kind.RELIABLE
                    );
                } catch (e) {
                    console.warn('Failed to broadcast stop approval request', e);
                }
                onRecordingStoppedMessage?.('Stop request sent to recording starter for approval.');
                return;
            }
            if (!res.ok) {
                let message = 'Recording could not be stopped.';
                try {                    
                    const errText = body.error || body.message || body.detail;
                    if (typeof errText === 'string' && errText.trim()) message = errText;
                } catch (_) {
                    if (text && text.length < 200 && !text.startsWith('{')) message = text;
                }
                alert(message);
                return;
            }
        } catch (err) {
            console.error('Stop recording failed', err);
            alert('Could not stop recording. Please try again.');
            return;
        }
        if (recordingStarterIdentity && recordingStarterIdentity !== room.localParticipant.identity) {
            onRecordingStoppedMessage?.('Recording stop approved by starter.');
        }
        setRecording(false);
        setEgressId(null);
        onStopRecording?.();
        onRecordingStoppedMessage?.('Recording has been stopped. Final recording will be available in a few moments.');
        // Broadcast so all participants hide the recording indicator
        try {
            room.localParticipant.publishData(
                new TextEncoder().encode(
                    JSON.stringify({ type: 'recordingStatus', recording: false, starterIdentity: null })
                ),
                DataPacket_Kind.RELIABLE
            );
        } catch (e) {
            console.warn('Failed to broadcast recording stopped', e);
        }
    };

    // Show "Stop Rec" when we started recording OR when server/room says recording is active (e.g. starter dropped)
    const recordingActive = Boolean(isRecordingActive ?? recording);

    const toggleWakeLock = async () => {
        if (wakeLock) {
            wakeLock.release();
            setWakeLock(null);
            setWakeLockNotice(null);
            return;
        }
        try {
            const api = navigator.wakeLock;
            if (!api?.request) {
                setWakeLockNotice(
                    'Keeping the screen on is not supported in this browser. On many phones the OS may still dim or lock the screen; try a shorter auto-lock or stay on this tab.'
                );
                return;
            }
            const wl = await api.request('screen');
            wl.addEventListener('release', () => setWakeLock(null));
            setWakeLock(wl);
            setWakeLockNotice(null);
        } catch (err) {
            console.warn('wakeLock', err);
            setWakeLockNotice(
                'Could not keep the screen awake. Tap the page and try again, or turn off screen lock briefly—the call may drop if the device locks.'
            );
        }
    };

    return (
        <>
            {wakeLockNotice && (
                <div className="controls-wake-toast" role="status" aria-live="polite">
                    <span className="controls-wake-toast__text">{wakeLockNotice}</span>
                    <button
                        type="button"
                        className="controls-wake-toast__dismiss"
                        onClick={() => setWakeLockNotice(null)}
                        aria-label="Dismiss"
                    >
                        ×
                    </button>
                </div>
            )}
        <div className="controls-bar">
            <button
                className={`control-btn ${micOn ? '' : 'off'}`}
                onClick={toggleMic}
            >
                {micOn ? '🎙 Mic' : '🔇 Mic'}
            </button>

            <button
                className={`control-btn ${camOn ? '' : 'off'}`}
                onClick={toggleCam}
            >
                {camOn ? '📷 Cam' : '🚫 Cam'}
            </button>

            <button
                className={`control-btn ${isLocalScreenSharer ? 'active' : ''
                    }`}
                onClick={toggleScreen}
            >
                {isLocalScreenSharer ? '🛑 Stop Share' : '🖥 Share'}
            </button>

            {showAudioOutputPicker && onAudioSinkIdChange && (
                <select
                    className="control-select"
                    aria-label="Meeting audio: speaker auto or specific device"
                    value={audioSinkId || ''}
                    onChange={(e) => onAudioSinkIdChange(e.target.value)}
                    title={
                        typeof window !== 'undefined' && window.isSecureContext === false
                            ? 'HTTPS may be required for audio output selection in some browsers.'
                            : 'Speaker (auto) tries the loudspeaker on phones. Pick another device if sound is wrong.'
                    }
                >
                    <option value="">Speaker (auto)</option>
                    {audioOutputDevices.map((d) => (
                        <option key={d.deviceId} value={d.deviceId}>
                            {d.label && d.label.trim()
                                ? d.label
                                : `Output ${d.deviceId.slice(0, 8)}…`}
                        </option>
                    ))}
                </select>
            )}
            {!showAudioOutputPicker &&
                typeof window !== 'undefined' &&
                window.matchMedia?.('(pointer: coarse)')?.matches && (
                    <span
                        className="control-audio-hint"
                        role="note"
                        title={
                            isLikelyIOS()
                                ? 'Turn volume up. If silent switch shows orange, flip it off. Unmute Mic so others hear you. Safari cannot pick speaker vs earpiece here.'
                                : 'Turn volume up. For a speaker/earpiece menu, use Chrome on Android where supported.'
                        }
                    >
                        {isLikelyIOS()
                            ? '🔊 iOS: volume up · unmute Mic'
                            : '🔊 Volume up · Chrome for speaker menu'}
                    </span>
                )}

            {'wakeLock' in navigator && (
                <button
                    type="button"
                    className={`control-btn ${wakeLock ? 'active' : ''}`}
                    onClick={toggleWakeLock}
                    title="Reduce disconnects when the screen turns off (best-effort; iOS limits apply)"
                >
                    {wakeLock ? '☀ Screen on' : '⏻ Keep awake'}
                </button>
            )}

            <button
                className={`control-btn ${chatOpen ? 'active' : ''}`}
                onClick={() => {
                    setChatOpen(v => !v);
                    setUnreadCount(0);
                }}
            >
                💬 Chat
                {unreadCount > 0 && (
                    <span className="badge">{unreadCount}</span>
                )}
            </button>
            <button
                className={`control-btn ${recordingActive ? 'active' : ''}`}
                onClick={recordingActive ? stopRecording : startRecording}
            >
                {recordingActive ? '⏹ Stop Rec' : '⏺ Record'}
            </button>

            <button
                className="control-btn leave"
                onClick={() => room.disconnect()}
            >
                🚪 Leave
            </button>
        </div>
        </>
    );
}
